import React from 'react';
import { am5, am5themes_Animated, am5xy, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { createAxes } from './methods/create-axes';
import { createSeries } from './methods/create-series';
import { getScaledData } from './methods/get-scaled-data';
import { addLabels } from '../shared/add-labels';
import { createBaseTooltips } from '../shared/create-base-tooltips';
import { createDownloadButton } from '../shared/create-download-button';
import { handleZoom } from '../shared/handle-zoom';
import { getPaddingLeft } from '../shared/helpers';
import { useChartOnReady } from '../shared/use-chart-on-ready';

export type ChartRef = {
  series: any[] | null;
  seriesKeys: string[];
  onReady: (_callback: (_series: any[]) => void) => void;
};

export type ChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  xAxisField: string;
  yAxisField: string;
  percent: boolean;
  bucket: string | undefined;
  showError: boolean;
  showBucketOnTooltip: boolean;
  yAxisLabel: string | undefined;
  xAxisLabel: string | undefined;
  download: boolean;
  formatData?: (_data: types.widgets.ErrorLinearData) => types.widgets.ErrorLinearData;
  seriesColorByName: Record<string, string>;
};

const Chart = React.forwardRef<ChartRef, ChartProps>(
  (
    {
      id,
      data,
      fullScreen,
      xAxisField,
      yAxisField,
      percent,
      bucket,
      showError,
      showBucketOnTooltip,
      yAxisLabel,
      xAxisLabel,
      download,
      formatData,
      seriesColorByName,
    },
    ref
  ) => {
    const widgetId = `chart-${id}-${fullScreen ? 'fs' : 'sw'}`;
    const theme = mui.styles.useTheme() as mui.core.Theme;
    const [seriesState, setSeriesState] = React.useState<any[] | null>(null);
    const [seriesKeysState, setSeriesKeysState] = React.useState<string[]>([]);
    const { registerCallback, executeCallbacks } = useChartOnReady();

    React.useImperativeHandle(
      ref,
      () => ({
        series: seriesState,
        seriesKeys: seriesKeysState,
        onReady: registerCallback,
      }),
      [seriesState, seriesKeysState, registerCallback]
    );

    React.useEffect(() => {
      // Create root element
      const root = am5.Root.new(widgetId);

      // Set themes
      root.setThemes([am5themes_Animated.new(root)]);

      // Remove amCharts logo
      root._logo?.dispose();

      const [errorData, minLimit, maxLimit] = getScaledData(
        data as types.widgets.TableData,
        xAxisField,
        yAxisField,
        showError ? 'error' : undefined,
        bucket,
        !percent
      );

      let formattedErrorData = errorData;
      if (formatData) formattedErrorData = formatData(errorData);

      const seriesKeys = Object.keys(formattedErrorData ?? {});
      setSeriesKeysState(seriesKeys);

      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelY: 'zoomX',
          wheelX: 'panX',
          pinchZoomX: false,
          layout: root.verticalLayout,
          maxTooltipDistance: 0,
          paddingLeft: getPaddingLeft(download, yAxisLabel),
        })
      );

      const { xAxis, yAxis } = createAxes({
        root,
        chart,
        minLimit,
        maxLimit,
      });

      const createdSeries = createSeries({
        root,
        chart,
        data: formattedErrorData,
        xAxisField,
        yAxisField,
        xAxis,
        yAxis,
        showBucketOnTooltip,
        percent,
        showError,
        seriesColorByName,
      });

      setSeriesState(createdSeries);

      chart.set(
        'cursor',
        am5xy.XYCursor.new(root, {
          xAxis: xAxis,
          yAxis: yAxis,
        })
      );

      createBaseTooltips({ root, chart, xAxis, yAxis, theme });
      handleZoom(chart);
      if (download) createDownloadButton(root, xAxisField, data);
      addLabels({ root, yAxis, xAxis, yAxisLabel, xAxisLabel });

      chart.appear(1000, 100);

      // Execute callbacks when chart has actually finished rendering and animating
      executeCallbacks(createdSeries);

      return () => {
        setSeriesState(null);
        setSeriesKeysState([]);
        root.dispose();
      };
    }, [data, showError, seriesColorByName]);

    return (
      <mui.core.Box
        sx={{
          width: '100%',
          height: '100%',
        }}
      >
        <div
          id={widgetId}
          style={{
            width: '100%',
            height: '100%',
          }}
        />
      </mui.core.Box>
    );
  }
);

export default Chart;
